import { supabase } from './supabaseClient'

export type SuggestedSlot = {
  label: string
  start: string
  end: string
}

export type ConflictEventSide = {
  title: string
  startsAt: string
  endsAt: string
  kind: 'workout' | 'calendar'
  isWorkEvent?: boolean
}

export type ConflictAlert = {
  id: string
  message: string
  kilo_block_id: string | null
  occurrence_date: string | null
  suggested_slots: SuggestedSlot[]
  workout: ConflictEventSide | null
  calendarEvent: ConflictEventSide | null
  overlapMinutes: number
}

function parseSuggestedSlots(raw: unknown): SuggestedSlot[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((slot) => {
      if (!slot || typeof slot !== 'object') return null
      const row = slot as Record<string, unknown>
      const label = String(row.label ?? '').trim()
      const start = String(row.start ?? '').trim()
      const end = String(row.end ?? '').trim()
      if (!label || !start || !end) return null
      return { label, start, end }
    })
    .filter((slot): slot is SuggestedSlot => slot !== null)
}

function scheduleToIso(date: string, time: string): string {
  const normalized = time.trim().length === 5 ? `${time.trim()}:00` : time.trim()
  return new Date(`${date}T${normalized}`).toISOString()
}

function overlapMinutes(aStart: string, aEnd: string, bStart: string, bEnd: string): number {
  const start = Math.max(new Date(aStart).getTime(), new Date(bStart).getTime())
  const end = Math.min(new Date(aEnd).getTime(), new Date(bEnd).getTime())
  if (end <= start) return 0
  return Math.round((end - start) / 60_000)
}

export function formatConflictTimeRange(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const sameDay = start.toDateString() === end.toDateString()
  const dateLabel = start.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const time = (value: Date) =>
    value.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  if (sameDay) return `${dateLabel} · ${time(start)} – ${time(end)}`
  return `${time(start)} – ${time(end)}`
}

export async function loadPendingConflictAlert(userId: string): Promise<{ alert: ConflictAlert | null; error: string | null }> {
  const { alerts, error } = await loadPendingConflictAlerts(userId)
  if (error) return { alert: null, error }
  return { alert: alerts[0] ?? null, error: null }
}

export async function loadPendingConflictAlerts(
  userId: string,
): Promise<{ alerts: ConflictAlert[]; error: string | null }> {
  const { data, error } = await supabase
    .from('calendar_conflict_alerts')
    .select('id, message, kilo_block_id, suggested_slots, conflicting_event_id, occurrence_date')
    .eq('user_id', userId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })

  if (error) return { alerts: [], error: error.message }
  if (!data?.length) return { alerts: [], error: null }

  const alerts = await Promise.all(
    data.map(async (row) => {
      const [workoutResult, calendarResult] = await Promise.all([
        row.kilo_block_id
          ? supabase
              .from('training_schedules')
              .select('block_title, scheduled_date, start_time, end_time')
              .eq('id', row.kilo_block_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        row.conflicting_event_id
          ? supabase
              .from('external_calendar_events')
              .select('title, starts_at, ends_at, is_work_event')
              .eq('id', row.conflicting_event_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ])

      if (workoutResult.error || calendarResult.error) return null

      const workout = workoutResult.data
        ? {
            title: workoutResult.data.block_title,
            startsAt: scheduleToIso(
              row.occurrence_date ?? workoutResult.data.scheduled_date,
              workoutResult.data.start_time,
            ),
            endsAt: scheduleToIso(
              row.occurrence_date ?? workoutResult.data.scheduled_date,
              workoutResult.data.end_time,
            ),
            kind: 'workout' as const,
          }
        : null

      const calendarEvent = calendarResult.data
        ? {
            title: calendarResult.data.title,
            startsAt: calendarResult.data.starts_at,
            endsAt: calendarResult.data.ends_at,
            kind: 'calendar' as const,
            isWorkEvent: Boolean(calendarResult.data.is_work_event),
          }
        : null

      const overlap =
        workout && calendarEvent
          ? overlapMinutes(workout.startsAt, workout.endsAt, calendarEvent.startsAt, calendarEvent.endsAt)
          : 0

      return {
        id: row.id,
        message: row.message,
        kilo_block_id: row.kilo_block_id,
        occurrence_date: row.occurrence_date ?? null,
        suggested_slots: parseSuggestedSlots(row.suggested_slots),
        workout,
        calendarEvent,
        overlapMinutes: overlap,
      } satisfies ConflictAlert
    }),
  )

  return {
    alerts: alerts.filter((alert): alert is NonNullable<typeof alert> => alert !== null),
    error: null,
  }
}

function isoToTime(iso: string): string {
  const date = new Date(iso)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

export async function rescheduleConflict(
  alertId: string,
  blockId: string,
  slot: SuggestedSlot,
  occurrenceDate?: string | null,
): Promise<{ error: string | null }> {
  const updates: Record<string, string> = {
    start_time: isoToTime(slot.start),
    end_time: isoToTime(slot.end),
  }
  if (occurrenceDate) {
    updates.scheduled_date = occurrenceDate
  }

  const { error: scheduleError } = await supabase
    .from('training_schedules')
    .update(updates)
    .eq('id', blockId)

  if (scheduleError) return { error: scheduleError.message }

  const { error: alertError } = await supabase.from('calendar_conflict_alerts').update({ status: 'resolved' }).eq('id', alertId)

  return { error: alertError?.message ?? null }
}

export async function dismissConflict(alertId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('calendar_conflict_alerts').update({ status: 'dismissed' }).eq('id', alertId)
  return { error: error?.message ?? null }
}
